import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { DynamicPricingGroupResponseSchema } from "../response/IndexDynamicPricingGroupResponse";

/**
 * Mendefinisikan properti yang bisa diterima oleh hook.
 */
interface IndexDynamicPricingGroupsProps {
    search?: string;
    orderBy?: string;
    sort?: 'asc' | 'desc';
    limit?: number;
    page?: number;
    enabled?: boolean;
    property_id?: string; // Filter spesifik untuk fitur ini
    include?: string; // Untuk memuat relasi, misal: 'property,items'
    is_active?: boolean; // Filter untuk hanya mengambil grup yang aktif
}

/**
 * Custom hook untuk mengambil daftar Dynamic Pricing Group dari API.
 * @param query - Objek yang berisi parameter untuk kustomisasi query API.
 * @returns Hasil dari useQuery.
 */
const useIndexDynamicPricingGroups = (query: IndexDynamicPricingGroupsProps) => {
    return useQuery({
        queryKey: ['dynamic-pricing-groups', query],

        queryFn: async () => {
            try {
                const params: Record<string, any> = {
                    'filter[property_id]': query.property_id,
                    'filter[is_active]': query.is_active,
                    search: query.search,
                    orderBy: query.orderBy ?? 'created_at',
                    sort: query.sort ?? 'desc',
                    limit: query.limit ?? 10,
                    page: query.page,
                    include: query.include,
                };

                // Membersihkan parameter yang tidak memiliki nilai
                Object.keys(params).forEach(key =>
                    (params[key] === undefined || params[key] === null) && delete params[key]
                );

                const response = await privateApi.get(`/v4/dynamic-pricing/group`, { params });

                const validationResult = DynamicPricingGroupResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("API Response Validation Failed:", validationResult.error.errors);
                    throw new Error("Invalid data format received from the server.");
                }

                return validationResult.data;
            } catch (error) {
                console.error("Failed to fetch dynamic pricing groups:", error);
                throw error;
            }
        },
        enabled: query.enabled !== false,
        retry: 1,
        refetchOnWindowFocus: false,
    });
};

export default useIndexDynamicPricingGroups;